import {RouteDefinition} from '../../routes';
import {matchPath, Route, Switch, useHistory, useLocation} from 'react-router-dom';
import {Header} from '../Header';
import {Sidebar} from '../Sidebar';
import {MenuItem} from '../MenuItem';
import {Footer} from '../Footer';
import {CallScreen} from '../../screens/CallScreen';
import {PopupScreen} from '../../screens/PopupScreen';
import React, {memo, ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {Viewport} from '../Viewport';
import {ScenarioCreateScreen} from '../../screens/ScenarioCreateScreen';

import './index.scss';
import {ScenarioPopupScreen} from '../../screens/ScenarioPopupScreen';
import {MediaScreen} from '../../screens/MediaScreen';
import {ScenarioTriggerCreateScreen} from '../../screens/ScenarioTriggerCreateScreen';
import {ScenarioTriggerPopupScreen} from '../../screens/ScenarioTriggerPopupScreen';
import {useRPC} from '../../hooks/rpc';
import {ScenarioTriggerControlUpdateScreen} from '../../screens/ScenarioTriggerControlUpdateScreen';
import {NotFound404} from '../NotFound404';
import {BusyIndicator} from '../BusyIndicator';
import {MenuEntity} from '../../store/control/state';
import {leftMenuIconList} from '../../screens/Settings/Customization/SortingsMenu/LeftMenu/LeftMenuEditScreen';
import {useBackground} from '../../hooks/background-class';
import {StpUIMediaScreen} from '../../screens/StpUIMediaScreen';
import {useLocalization} from '../../hooks/localization';
import {useSelector} from 'react-redux';
import {ScenarioFooterPopupScreen} from '../../screens/ScenarioFooterPopupScreen';
import {PopupPasswordScreen} from '../../screens/PopupPasswordScreen';
import {ResetButtonPopover} from '../ResetButtonPopup';

type Props = {
    busy: boolean;
    routes: RouteDefinition[];
    leftMenuList: MenuEntity[];
    locked: boolean;
    lockedReason: string;
    isPc: boolean;
    scenarioCreateScreenVisible: boolean;
    scenarioTriggerCreateScreenVisible: boolean;
    scenarioTriggerControlUpdateScreenVisible: boolean;
    mediaScreenVisible: boolean;
    popupScreenVisible: boolean;
    popupAlarmPasswordScreenVisible: boolean;
    applicationLoaded: boolean;
    stpUIMediaScreenVisible: boolean;
};
type ReactNodeOrRenderer = (() => ReactNode);

declare function renderIf(predicate: any): (item: ReactNodeOrRenderer, elseValue?: ReactNodeOrRenderer) => ReactNode;

export const AppComponent = memo(function AppComponent(props: Props) {
    const {
        routes,
        leftMenuList,
        busy,
        locked,
        lockedReason,
        isPc,
        applicationLoaded,
        scenarioCreateScreenVisible,
        scenarioTriggerCreateScreenVisible,
        scenarioTriggerControlUpdateScreenVisible,
        mediaScreenVisible,
        popupScreenVisible,
        popupAlarmPasswordScreenVisible,
        stpUIMediaScreenVisible,
    } = props;

    const popupVisible = scenarioCreateScreenVisible || mediaScreenVisible || stpUIMediaScreenVisible || scenarioTriggerCreateScreenVisible || scenarioTriggerControlUpdateScreenVisible;
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const [, , backgroundStyle] = useBackground();
    const {t} = useLocalization();
    const resetPasswordList = useSelector(state => state.control.userResetPassword)
    const history = useHistory();
    const defaultAlarmState = useSelector(state => state.control.alarmState.default)
    const controlList = useSelector(state => state.control.controlList)
    const indicatorStatus = useSelector(state => state.control.config.indicator_status)
    const userControl = useSelector(state => state.user.username)
    const resetButtonStatus = useSelector(state => state.control.somResetCountdown)

    const location = useLocation();
    const [rpc, isConnected, retryCount] = useRPC();

    const touchEventHandler = useCallback(() => {
        rpc.touchEvent().then();
    },[rpc]);

    const statusIndicator = useCallback(
        (id: string, specialId: string, color?: string) => {
            const selectControl = controlList.filter(
                control =>
                    control?.type_id === "1" ||
                    control?.type_id === "2" ||
                    control?.type_id === "22" ||
                    control?.type_id === "23"
            )
            const currentValueStatus = selectControl.filter(value => value?.current_value === 1)
            if (id === "alarm") {
                if (defaultAlarmState?.level === 0) {
                    return t("customizations.disabled")
                } else if (defaultAlarmState?.level === 1) {
                    return t("customizations.basic")
                } else if (defaultAlarmState?.level === 2) {
                    return t("customizations.indoor")
                } else if (defaultAlarmState?.level === 3) {
                    return t("customizations.outdoor")
                } else {
                    return "-"
                }
            } else if (specialId === 'lamp') {
                return currentValueStatus.length.toString() || "0"
            }else{
                return ''
            }
        },
        [t, defaultAlarmState, controlList]
    )

    const createNewMenuList = useMemo(() => {
        const list = []
        const filteredName = (id: string) => {
            for (const value of leftMenuList) {
                switch (id) {
                    case "area":
                        return (value.name = t("left_menu_title.floors_rooms"))
                    case "intercom":
                        return (value.name = t("left_menu_title.intercom"))
                    case "alarm":
                        return (value.name = t("left_menu_title.alarm"))
                    case "scenario":
                        return (value.name = t("left_menu_title.scenario"))
                    case "sensor":
                        return (value.name = t("left_menu_title.sensors"))
                    case "camera":
                        return (value.name = t("left_menu_title.camera"))
                    case "media-remote":
                        return (value.name = t("left_menu_title.media"))
                    case "concierge":
                        return (value.name = t("left_menu_title.concierge"))
                    case "settings":
                        return (value.name = t("left_menu_title.settings"))
                    case "all-controls":
                        return (value.name = t("left_menu_title.all_controls"))
                    case "energy":
                        return (value.name = t("left_menu_title.energy"))
                }
            }
        }
        for (const values of leftMenuList) {
            if (values.is_hidden) continue;
            list.push({
                id: values.id,
                name: filteredName(values.id) || values.name,
                onClick: values.onClick,
                order: values.order,
                content_list: values.content_list,
                icon: values.icon,
                is_special: values.is_special
            })
        }
        return list
    }, [leftMenuList, t])


    const inactiveUser = useSelector(state => state.control.temporaryUserStatus)
     //const selectedRole = useSelector(state => state.ui.role)
    const userList = useSelector(state => state.control.userList)
    const currentUsername = useSelector(state => state.user.username)
    const currentUser = useMemo(() => userList.find(u => u.username === currentUsername), [currentUsername, userList])
    useEffect(() => {
        if (inactiveUser && currentUser?.role === "user") {
            window.location.reload()
        }
     }, [inactiveUser, currentUser])

    useEffect(() => {
        if (userControl !== "default" && currentUser?.role !== "developer" && currentUser?.role !== "service") {
            if (resetPasswordList?.first_login && location.pathname !== "/reset-password") {
                return history.push("/reset-password")
            }
        }
    }, [resetPasswordList, history, location, userControl, currentUser])

    useEffect(() => {
        const match = matchPath(location.pathname, {
            path: '/settings',
            exact: false,
            strict: false,
        });
        const match2 = matchPath(location.pathname, {
            path: '/developer',
            exact: false,
            strict: false,
        });
        setSidebarVisible(isConnected && match == null && match2 == null);

    }, [location, isConnected]);

    return (
        <div
            className={'main' + (popupVisible ? ' popupVisible' : '') + (isPc ? ' pc' : '')} style={backgroundStyle} onClick={touchEventHandler}>
            <Header locked={locked}/>
            {
                renderIf(locked)(() =>
                    <div className='locked-reason-container'>
                        <span>{lockedReason}</span>
                    </div>,
                )
            }
            {
                renderIf(sidebarVisible)(() =>
                    <Sidebar locked={locked}>
                        {
                            createNewMenuList.map(leftMenu => {
                                return (
                                    <MenuItem
                                        key={leftMenu.name}
                                        routerLink={leftMenu.is_special ? `/${leftMenu.id}` : `/page/${leftMenu.id}`}
                                        exact={true}
                                        title={leftMenu.name}
                                        onClick={leftMenu.onClick}
                                        status={statusIndicator(leftMenu.id, leftMenu.icon)}
                                        isActive={indicatorStatus}
                                        iconUrl={leftMenuIconList.find(l => l.value.name === leftMenu.icon)?.value?.iconUrl}/>
                                );
                            })
                        }
                    </Sidebar>,
                )
            }

            <Viewport isConnected={isConnected} retryCount={retryCount}>
                {
                    renderIf(applicationLoaded)(() =>
                        <Switch>
                            {
                                routes.filter(route => route.routerLink).map(
                                    route => (
                                        <Route
                                            key={route.routerLink}
                                            path={route.routerLink}
                                            exact={true}
                                            component={route.component}/>
                                    ),
                                )
                            }
                            <Route
                                path='*'
                                exact={true}
                                component={NotFound404}/>
                        </Switch>,
                    )
                }
            </Viewport>
            <Footer locked={locked}/>

            <CallScreen/>
            {renderIf(popupScreenVisible)(() => <PopupScreen/>)}
            {renderIf(popupAlarmPasswordScreenVisible)(() => <PopupPasswordScreen/>)}
            <ScenarioFooterPopupScreen/>
            <ScenarioPopupScreen/>
            <ScenarioTriggerPopupScreen/>
            {renderIf(scenarioCreateScreenVisible)(() => <ScenarioCreateScreen/>)}
            {renderIf(scenarioTriggerCreateScreenVisible)(() => <ScenarioTriggerCreateScreen/>)}
            {renderIf(scenarioTriggerControlUpdateScreenVisible)(() => <ScenarioTriggerControlUpdateScreen/>)}
            {renderIf(mediaScreenVisible)(() => <MediaScreen/>)}
            {renderIf(stpUIMediaScreenVisible)(() => <StpUIMediaScreen/>)}
            {renderIf(busy)(() => <BusyIndicator/>)}
            {resetButtonStatus !== '' && <ResetButtonPopover />}
        </div>
    );
});
